import React, { useState } from 'react';
import { Shield, Monitor, AlertCircle, Loader2 } from 'lucide-react';

interface TDSEntryProps {
  onCodeVerified: (businessData: any) => void;
}

const TDSEntry: React.FC<TDSEntryProps> = ({ onCodeVerified }) => {
  const [tdsCode, setTdsCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);

  const formatTDSCode = (value: string) => {
    // Remove all non-alphanumeric characters and uppercase
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    // Support both SD and TDS prefixes while typing/pasting
    // Prefer SD-XXXX-XXXX format for business codes
    if (cleaned.startsWith('SD')) {
      if (cleaned.length <= 2) return cleaned; // SD
      if (cleaned.length <= 6) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`; // SD-XXXX
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}-${cleaned.slice(6, 10)}`; // SD-XXXX-XXXX
    }

    // Legacy support for TDS-XXXX-XXXX
    if (cleaned.startsWith('TDS')) {
      if (cleaned.length <= 3) return cleaned; // TDS
      if (cleaned.length <= 7) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`; // TDS-XXXX
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7, 11)}`; // TDS-XXXX-XXXX
    }

    // If no prefix yet, steer toward SD format as user types
    if (cleaned.length <= 2) return cleaned; // up to SD
    if (cleaned.length <= 6) return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatTDSCode(e.target.value);
    setTdsCode(formatted);
    setError(null);
    setIsValid(false);
  };

  const verifyTDSCode = async () => {
    if (!tdsCode || tdsCode.length < 8) {
      setError('Please enter a complete SD or TDS code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔍 Verifying TDS code:', tdsCode);
      
      const response = await fetch(`/api/business/verify-tds/${tdsCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ TDS code verified successfully:', data.data);
        setIsValid(true);
        setError(null);
        
        // Store business data in localStorage for KDS session
        localStorage.setItem('kds_business_data', JSON.stringify(data.data));
        localStorage.setItem('kds_tds_code', tdsCode);
        
        // Notify parent component
        onCodeVerified(data.data);
      } else {
        setError(data.message || 'Invalid TDS code. Please check and try again.');
        setIsValid(false);
      }
    } catch (error) {
      console.error('❌ Error verifying TDS code:', error);
      setError('Network error. Please check your connection and try again.');
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verifyTDSCode();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      verifyTDSCode();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#7de2c5]/20 to-[#6dd1b4]/30 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-[#7de2c5]/20 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <Monitor className="w-10 h-10 text-[#7de2c5]" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            SmartDry TDS Access
          </h1>
          <p className="text-gray-600 text-sm">
            Enter your TDS code to access your SmartDry Display System.
          </p>
        </div>

        {/* TDS Code Entry Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              TDS Code
            </label>
            
            {/* Quick paste button */}
            <div className="mb-2">
              <button
                type="button"
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    if (text && (text.toUpperCase().includes('SD-') || text.toUpperCase().includes('TDS-'))) {
                      const formatted = formatTDSCode(text);
                      setTdsCode(formatted);
                      setError(null);
                    }
                  } catch (e) {
                    // Clipboard access failed, ignore
                  }
                }}
                className="text-xs text-black hover:text-gray-700 underline"
              >
                📋 Paste SD/TDS Code from Clipboard
              </button>
            </div>
            <div className="relative">
              <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={tdsCode}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="SD-XXXX-XXXX"
                className={`w-full pl-10 pr-4 py-3 border rounded-lg text-center font-mono text-lg tracking-wider transition-colors ${
                  error 
                    ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500' 
                    : isValid
                    ? 'border-green-300 bg-green-50 focus:border-green-500 focus:ring-green-500'
                    : 'border-gray-300 focus:border-[#7de2c5] focus:ring-[#7de2c5]'
                }`}
                maxLength={12}
                disabled={loading}
              />
            </div>
            
            {/* Validation Messages */}
            {error && (
              <div className="mt-2 flex items-center space-x-2 text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            
            {isValid && (
              <div className="mt-2 flex items-center space-x-2 text-green-600">
                <Shield className="w-4 h-4" />
                <span className="text-sm">Code verified successfully!</span>
              </div>
            )}
          </div>

          {/* Submit Button */}
                      <button
              type="submit"
              disabled={loading || !tdsCode || tdsCode.length < 8}
            className={`w-full py-3 rounded-lg font-medium transition-all duration-200 ${
              loading || !tdsCode || tdsCode.length < 8
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#7de2c5] text-white hover:bg-[#6dd1b4] transform hover:scale-[1.02] active:scale-[0.98]'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Verifying...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <Monitor className="w-5 h-5" />
                <span>Access KDS</span>
              </div>
            )}
          </button>
        </form>

        {/* Information Section */}
        <div className="mt-8 p-4 bg-[#7de2c5]/10 rounded-lg">
          <h3 className="text-sm font-semibold text-green-800 mb-2">
            What is a TDS Code?
          </h3>
          <ul className="text-green-800 text-xs space-y-1">
            <li>• TDS = Terminal Display System</li>
            <li>• Unique code for your business</li>
            <li>• Ensures secure access to your orders</li>
            <li>• Generated from your Settings page</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Need help? Contact your system administrator or generate a new TDS code from your Settings page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TDSEntry;
